import {
  type RouteLocationNormalized,
  type RouteRecordRaw,
  createRouter,
  createWebHistory,
} from 'vue-router'
import homeLayout from '@/layout/homeLayout.vue'
import useTabs from '@/hooks/useTabs'
import useMenu from '@/hooks/useMenu'
import { IS_GLOBAL } from '@/const'
// 充值记录地址
const PAY_RECORD_URL = IS_GLOBAL
  ? `${import.meta.env.VITE_USER_CENTER}user-center/assets/recharge-records`
  : `${import.meta.env.VITE_USER_CENTER}main/bill/ent/ent-pay?merchandiseType=1`
// 开票记录地址
const INVOICE_RECORD_URL = `${import.meta.env.VITE_USER_CENTER}main/bill/ent/entInvoice?tabName=sea`

const pureLayout = () => import('@/layout/pureLayout.vue')
const { getTab, activeTabName } = useTabs()
const { activeMenuName } = useMenu()

const pageRoutes: RouteRecordRaw = {
  path: import.meta.env.VITE_ROUTE_PREFIX,
  name: 'Home',
  meta: {
    title: 'route.productName',
  },
  redirect: { name: 'Draft' },
  component: homeLayout,
  children: [
    {
      path: 'draft',
      name: 'Draft',
      meta: {
        title: 'route.draft',
        icon: 'icon-base-draft-filled',
      },
      component: () => import('@/views/draft/index.vue'),
    },

    {
      path: 'detail/:id',
      name: 'Detail',
      meta: {
        title: 'route.detail',
        hidden: true,
      },
      component: () => import('@/views/detail/index.vue'),
    },
    {
      path: 'send',
      name: 'Send',
      meta: {
        title: 'route.send',
        icon: 'icon-base-Issued-filled',
      },
      component: () => import('@/views/send/has-sent.vue'),
    },
    {
      path: 'sent-history/:id',
      name: 'SentHistory',
      meta: {
        title: 'route.historyRecord',
        hidden: true,
      },
      component: () => import('@/views/sentHistory/index.vue'),
    },
    {
      path: 'history-detail/:id',
      name: 'HistoryDetail',
      meta: {
        title: 'route.historyDetail',
        hidden: true,
      },
      component: () => import('@/views/sentHistory/detail.vue'),
    },
    {
      path: 'consume',
      name: 'ConsumeManager',
      meta: {
        title: 'route.bill',
        icon: 'icon-base-bill-filled',
      },
      component: pureLayout,
      children: [
        {
          path: 'consume-record',
          name: 'Consume',
          meta: {
            title: 'route.consumption',
          },
          component: () => import('@/views/consume-record/index.vue'),
        },
        {
          path: 'recharge-record',
          name: 'RechargeRecord',
          redirect: () => window.open(PAY_RECORD_URL) as any,
          meta: {
            title: 'route.recharge',
          },
        },
        {
          path: 'billing-record',
          name: 'BillingRecord',
          redirect: () => window.open(INVOICE_RECORD_URL) as any,
          meta: {
            title: 'route.invoice',
            hidden: IS_GLOBAL,
          },
        },
      ],
    },
    {
      path: 'push-settings',
      name: 'PushSettings',
      meta: {
        title: 'route.pushSettings',
        icon: 'icon-base-push-filled',
      },
      component: () => import('@/views/push-settings/index.vue'),
    },
    {
      path: 'user-guide',
      name: 'UserGuide',
      meta: {
        title: 'route.manual',
        icon: 'icon-base-guide-filled',
      },
      component: () => import('@/views/user-guide/index.vue'),
    },
  ],
}

const routes: RouteRecordRaw[] = [
  {
    path: '/landing-page',
    name: 'LandingPage',
    meta: {
      title: 'route.landingPage',
      hidden: true,
    },
    component: () => import('@/views/landing-page/index.vue'),
  },
  pageRoutes,
  {
    path: '/404',
    name: '404',
    meta: {
      hidden: true,
      title: 'route.404',
    },
    component: () => import('@/views/404/index.vue'),
  },
  // 所有未定义路由，全部重定向到404页
  {
    path: '/:pathMatch(.*)',
    redirect: { name: 'Draft' },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

/**
 * 处理AOS显示错误
 * 该函数根据提供的路由信息，调整应用主容器的样式。
 *
 * @param to - 路由位置的标准化对象，包含路由的详细信息。
 * 不返回任何值，其主要作用是基于提供的路由信息修改页面样式。
 */
const handleAOSError = (to: RouteLocationNormalized) => {
  const app = document.getElementById('app')
  if (!(to.name as string)?.includes('LandingPage')) {
    app?.setAttribute('style', 'height: 100%')
  } else {
    app?.setAttribute('style', 'height: auto')
  }
}

router.beforeEach(async (to, _from, next) => {
  handleAOSError(to)
  if (['Draft', 'Send'].includes(to.name as string)) {
    activeTabName.value = to.name as string
    activeMenuName.value = to.name as string
  } else {
    if (
      ['Detail', 'HistoryDetail', 'SentHistory', 'AI'].includes(
        to.name as string,
      )
    ) {
      const key = to.params.id as string
      const cache: any = getTab(key as string, to.name as string)
      if (cache) {
        activeTabName.value = key
      } else {
        activeTabName.value = activeMenuName.value
        next({ name: activeMenuName.value })
      }
    }
  }
  next()
})

router.onError((error, to) => {
  if (error.message.includes('Failed to fetch dynamically imported module')) {
    window.location.href = to.fullPath as string
  }
})

const ChildrenRoutes = pageRoutes.children

export { ChildrenRoutes }
export default router
